import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Smartphone, School, ChefHat, Building2, Clock, Shield, Zap, MessageSquare,
  TrendingUp, Award, FileText, Package, Calendar, DollarSign, BarChart3, FileCheck
} from 'lucide-react';
import NavigationControls from '../components/NavigationControls';
import PartieBadge from '../components/PartieBadge';
import { useLanguage } from '../contexts/LanguageContext';

export default function ProduitPage() {
  const { language } = useLanguage();

  const features = language === 'fr' ? [
    {
      category: 'Parent/Enfant',
      icon: Smartphone,
      color: 'lime',
      items: [
        { icon: Clock, label: 'Commande en 15 secondes' },
        { icon: Zap, label: 'Rappels automatiques' },
        { icon: Shield, label: 'Co-parentalité (permissions, split)' },
        { icon: Smartphone, label: 'Mode enfant (Avatars, Badges, Récompenses, Gamification)' }
      ]
    },
    {
      category: 'Traiteur',
      icon: ChefHat,
      color: 'cyan',
      items: [
        { icon: Package, label: 'Back-office complet' },
        { icon: Zap, label: 'Gestion automatisée' },
        { icon: Calendar, label: 'Gestion menus et production' },
        { icon: Shield, label: 'Intégration API (Comptabilité, Production, Communications)' }
      ]
    },
    {
      category: 'École',
      icon: School,
      color: 'orange',
      items: [
        { icon: TrendingUp, label: 'Portail quotidien temps réel' },
        { icon: Award, label: 'Suivi de satisfaction' },
        { icon: Zap, label: 'Alertes intelligentes' },
        { icon: FileText, label: 'Rapports automatisés' }
      ]
    },
    {
      category: 'Garderie',
      icon: Building2,
      color: 'purple',
      items: [
        { icon: Package, label: '1 commande globale pour 100% des enfants' },
        { icon: Calendar, label: 'Fréquence flexible : hebdomadaire ou mensuelle' },
        { icon: DollarSign, label: 'Abonnement automatique + choix de fréquence de paiement' },
        { icon: Zap, label: 'Gestion 100% autonome — aucun appel ni communication manuelle fragmentée' }
      ]
    },
    {
      category: 'Direction/CSS',
      icon: Building2,
      color: 'lime',
      items: [
        { icon: BarChart3, label: 'Portefeuille multi-établissements' },
        { icon: TrendingUp, label: 'Comparatifs et benchmarks' },
        { icon: Shield, label: 'Politiques et standards' },
        { icon: FileCheck, label: 'Escalades et audit trail' }
      ]
    }
  ] : [
    {
      category: 'Parent/Child',
      icon: Smartphone,
      color: 'lime',
      items: [
        { icon: Clock, label: '15-second ordering' },
        { icon: Zap, label: 'Automatic reminders' },
        { icon: Shield, label: 'Co-parenting (permissions, split)' },
        { icon: Smartphone, label: 'Child mode (Avatars, Badges, Rewards, Gamification)' }
      ]
    },
    {
      category: 'Caterer',
      icon: ChefHat,
      color: 'cyan',
      items: [
        { icon: Package, label: 'Complete back-office' },
        { icon: Zap, label: 'Automated management' },
        { icon: Calendar, label: 'Menu and production management' },
        { icon: Shield, label: 'API integration (Accounting, Production, Communications)' }
      ]
    },
    {
      category: 'School',
      icon: School,
      color: 'orange',
      items: [
        { icon: TrendingUp, label: 'Real-time daily portal' },
        { icon: Award, label: 'Satisfaction tracking' },
        { icon: Zap, label: 'Smart alerts' },
        { icon: FileText, label: 'Automated reports' }
      ]
    },
    {
      category: 'Daycare',
      icon: Building2,
      color: 'purple',
      items: [
        { icon: Package, label: 'Single global order for 100% of children' },
        { icon: Calendar, label: 'Flexible frequency: weekly or monthly' },
        { icon: DollarSign, label: 'Auto-subscription + payment frequency choice' },
        { icon: Zap, label: '100% autonomous management — no calls or fragmented manual communications' }
      ]
    },
    {
      category: 'Management/School Board',
      icon: Building2,
      color: 'lime',
      items: [
        { icon: BarChart3, label: 'Multi-facility portfolio' },
        { icon: TrendingUp, label: 'Comparisons and benchmarks' },
        { icon: Shield, label: 'Policies and standards' },
        { icon: FileCheck, label: 'Escalations and audit trail' }
      ]
    }
  ];

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      {/* Badge Partie */}
      <div className="mb-4 flex items-center gap-2 text-sm max-w-6xl mx-auto">
        <PartieBadge path="/produit" />
      </div>
      
      <div className="max-w-6xl mx-auto">
        <Card className="bg-black/60 border-lime-500/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl sm:text-3xl lg:text-4xl text-white mb-2">
              {language === 'fr' ? 'Produit & Expérience' : 'Product & Experience'}
            </CardTitle>
            <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-lime-500 to-cyan-500 rounded-full" />
          </CardHeader>
          <CardContent className="space-y-8">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              const colorClass = {
                lime: 'border-lime-500/30 from-lime-500/10',
                cyan: 'border-cyan-500/30 from-cyan-500/10',
                orange: 'border-orange-500/30 from-orange-500/10'
              }[feature.color];
              
              const iconColor = {
                lime: 'text-lime-400',
                cyan: 'text-cyan-400',
                orange: 'text-orange-400'
              }[feature.color];

              return (
                <Card key={idx} className={`bg-gradient-to-br ${colorClass} to-transparent border ${colorClass}`}>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <Icon className={`h-8 w-8 ${iconColor}`} />
                      <CardTitle className="text-white text-xl">{feature.category}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {feature.items.map((item, itemIdx) => {
                        const ItemIcon = item.icon;
                        return (
                          <div key={itemIdx} className="flex items-center gap-3 p-3 bg-black/40 rounded-lg">
                            <ItemIcon className={`h-5 w-5 ${iconColor} flex-shrink-0`} />
                            <span className="text-sm text-gray-300">{item.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Messagerie Interne - Encadré centralisé */}
            <Card className="bg-gradient-to-br from-lime-500/10 via-cyan-500/10 to-orange-500/5 border-lime-500/30 max-w-3xl mx-auto">
              <CardHeader>
                <div className="flex items-center justify-center gap-3">
                  <MessageSquare className="h-8 w-8 text-lime-400" />
                  <CardTitle className="text-white text-xl">{language === 'fr' ? 'Messagerie Interne Unifiée' : 'Unified Internal Messaging'}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-300 text-center leading-relaxed">
                  {language === 'fr' 
                    ? <>Communication sécurisée multi-niveaux avec <span className="text-lime-400 font-semibold">RBAC hiérarchique adaptatif</span> et permissions configurables.</>
                    : <>Multi-level secure communication with <span className="text-lime-400 font-semibold">adaptive hierarchical RBAC</span> and configurable permissions.</>}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Fonctionnalités */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-cyan-400 mb-2">{language === 'fr' ? 'Fonctionnalités' : 'Features'}</p>
                    <p className="text-xs text-gray-300 flex items-center gap-2">
                      <span className="text-lime-500">✓</span> {language === 'fr' ? 'Fichiers, photos, appels' : 'Files, photos, calls'}
                    </p>
                    <p className="text-xs text-gray-300 flex items-center gap-2">
                      <span className="text-cyan-500">✓</span> {language === 'fr' ? 'Canaux thématiques' : 'Thematic channels'}
                    </p>
                    <p className="text-xs text-gray-300 flex items-center gap-2">
                      <span className="text-orange-500">✓</span> {language === 'fr' ? 'Chiffrement & archivage' : 'Encryption & archiving'}
                    </p>
                  </div>

                  {/* RBAC */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-orange-400 mb-2">{language === 'fr' ? 'Contrôles RBAC' : 'RBAC Controls'}</p>
                    <p className="text-xs text-gray-300 flex items-center gap-2">
                      <span className="text-lime-500">•</span> {language === 'fr' ? 'École : active/désactive parents' : 'School: enable/disable parents'}
                    </p>
                    <p className="text-xs text-gray-300 flex items-center gap-2">
                      <span className="text-cyan-500">•</span> {language === 'fr' ? 'Traiteur : accès direct tous' : 'Caterer: direct access all'}
                    </p>
                    <p className="text-xs text-gray-300 flex items-center gap-2">
                      <span className="text-orange-500">•</span> {language === 'fr' ? 'Direction : mode actif OU observation' : 'Management: active OR observation mode'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <NavigationControls />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
